/* BEATSEEK — "Wrong edit or remix?" community correction report shown under an ID result. */
import React, { useState } from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = [['remix', 'Remix'], ['edit', 'Edit'], ['bootleg', 'Bootleg'], ['mashup', 'Mashup'], ['vip', 'VIP'], ['rework', 'Rework'], ['flip', 'Flip'], ['extended', 'Extended'], ['dub', 'Dub'], ['original', 'Original (engine was right about the mix)']];

const field = {
  width: '100%', padding: '7px 9px', borderRadius: 7, fontSize: '0.78rem',
  border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(0,0,0,0.35)', color: '#fff',
};

export const WrongEditReport = ({ track, isLoggedIn }) => {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ suggested_title: '', suggested_artist: '', suggested_version_type: 'remix', link: '', notes: '' });

  if (!track || !track.title || ['not_found', 'error', 'rate_limit'].includes(track.id)) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (track.correctedByCommunity) {
    return (
      <div data-testid="community-corrected-badge" style={{ marginTop: 8, textAlign: 'center', fontSize: '0.72rem', fontWeight: 400, color: 'rgba(0,229,255,0.75)' }}>
        <Check size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: '-1px' }} />
        Community-corrected version{track.correctionCredit ? ` · thanks ${track.correctionCredit}` : ''}
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.suggested_title.trim() && !form.suggested_artist.trim()) {
      return toast.error('Tell us the correct edit/remix name or who made it');
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/track-id/corrections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({
          attempt_id: track.attemptId || null,
          identified_title: track.title, identified_artist: track.artist,
          isrc: track.isrc || null, acr_id: track.acrId || null,
          ...form,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Could not send that');
      setSent(true); setOpen(false);
      toast.success(
        d.disputed_lock ? 'Report sent to a BEATSEEK admin' : 'Thanks — a BEATSEEK mod will review your correction',
        d.disputed_lock
          ? { description: 'This ID is locked in by other BeatSeekers, so it stays as-is until an admin reviews your report', duration: 7000 }
          : undefined,
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="wrong-edit-sent" style={{ marginTop: 8, textAlign: 'center', fontSize: '0.72rem', color: 'rgba(0,255,136,0.75)', fontWeight: 400 }}>
        Correction submitted — pending review
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        data-testid="wrong-edit-toggle"
        onClick={() => (isLoggedIn ? setOpen(true) : toast.error('Log in to suggest a correction'))}
        style={{
          display: 'block', margin: '8px auto 0', background: 'none', border: 0, padding: 0, cursor: 'pointer',
          fontSize: '0.72rem', fontWeight: 300, letterSpacing: '0.02em', color: 'rgba(255,255,255,0.42)',
          textDecoration: 'underline', textDecorationStyle: 'dotted', transition: 'color 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,45,120,0.9)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.42)'; }}
      >
        Wrong edit or remix? Let us know
      </button>
    );
  }

  return (
    <form onSubmit={submit} data-testid="wrong-edit-form" style={{ marginTop: 10, padding: 11, borderRadius: 10, background: 'rgba(255,45,120,0.06)', border: '1px solid rgba(255,45,120,0.25)', display: 'flex', flexDirection: 'column', gap: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 300 }}>
        <AlertCircle size={12} style={{ color: '#ff2d78' }} />
        Engine heard <strong style={{ color: '#fff', fontWeight: 600 }}>{track.artist} – {track.title}</strong>. What is it really?
      </div>
      <input value={form.suggested_title} onChange={set('suggested_title')} placeholder="Correct title · e.g. Glue (Sammy Virji Remix)" data-testid="wrong-edit-title" style={field} />
      <input value={form.suggested_artist} onChange={set('suggested_artist')} placeholder="Remixer / edit maker" data-testid="wrong-edit-artist" style={field} />
      <select value={form.suggested_version_type} onChange={set('suggested_version_type')} data-testid="wrong-edit-type" style={field}>
        {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <input value={form.link} onChange={set('link')} placeholder="Proof link (SoundCloud / tracklist) — optional" data-testid="wrong-edit-link" style={field} />
      <input value={form.notes} onChange={set('notes')} placeholder="Anything else we should know — optional" data-testid="wrong-edit-notes" style={field} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="submit" disabled={busy} data-testid="wrong-edit-submit" style={{ padding: '7px 16px', borderRadius: 999, border: '1px solid rgba(255,45,120,0.7)', background: 'rgba(255,45,120,0.18)', color: '#ff7aa8', fontSize: '0.76rem', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Sending…' : 'Send correction'}
        </button>
        <button type="button" onClick={() => setOpen(false)} style={{ background: 'none', border: 0, color: 'rgba(255,255,255,0.45)', fontSize: '0.74rem', cursor: 'pointer' }}>Cancel</button>
      </div>
      <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.35)', fontWeight: 300 }}>
        Approved corrections teach the engine — next time it hears this track it names your version.
      </span>
    </form>
  );
};

export default WrongEditReport;
